import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { CHANNEL_ICONS, CHANNEL_ICON_OPTIONS, CHANNEL_COLORS, CHANNEL_COLOR_OPTIONS } from "@/lib/channelStyle";
import ChannelPill from "@/components/settings/ChannelPill";

// Webhook-wired channel names — these get a special "Webhook" badge + warning.
const WEBHOOK_CHANNELS = new Set(["WhatsApp", "Email"]);

/**
 * Create or edit a channel. On edit with a name change, we lazily fetch the
 * ticket count and offer a cascade-update checkbox.
 */
export default function ChannelDialog({ open, onOpenChange, channel: target, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState({ name: "", icon: "MessageSquare", color: "slate", active: true });
  const [cascade, setCascade] = useState(false);
  const [ticketCount, setTicketCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({ name: target.name, icon: target.icon || "MessageSquare", color: target.color || "slate", active: target.active !== false });
    } else {
      setForm({ name: "", icon: "MessageSquare", color: "slate", active: true });
    }
    setCascade(false);
    setTicketCount(null);
  }, [target, open]);

  // Lazy-load ticket count when the user opens an edit dialog so the cascade
  // checkbox can show the impact number.
  useEffect(() => {
    if (open && isEdit && target?.id && ticketCount === null) {
      api.get(`/channels/${target.id}/ticket-count`)
        .then((r) => setTicketCount(r.data.ticket_count))
        .catch(() => setTicketCount(0));
    }
  }, [open, isEdit, target, ticketCount]);

  const nameChanged = isEdit && form.name.trim() && form.name.trim() !== target.name;
  const isWebhookWired = isEdit && WEBHOOK_CHANNELS.has(target.name);

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      if (isEdit) {
        const url = `/channels/${target.id}${nameChanged && cascade ? "?cascade=true" : ""}`;
        const { data } = await api.patch(url, form);
        const cascadeMsg = data.cascade_count > 0 ? ` · ${data.cascade_count} tickets renamed` : "";
        toast.success(`Channel updated${cascadeMsg}`);
      } else {
        await api.post("/channels", form);
        toast.success("Channel created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save channel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={isEdit ? "edit-channel-dialog" : "create-channel-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit channel" : "Create channel"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Channels group tickets by their inbound source. Pick an icon and colour so they're visually distinct everywhere they appear.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Live preview */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Preview</div>
              <ChannelPill channel={{ name: form.name || "Untitled", icon: form.icon, color: form.color }} size="lg" />
            </div>
            {isWebhookWired && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                <Link2 className="w-3 h-3" /> Webhook
              </span>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. WhatsApp"
              data-testid="channel-name-input"
              maxLength={60}
            />
          </div>

          {/* Icon picker */}
          <div className="space-y-1.5">
            <Label>Icon</Label>
            <div className="grid grid-cols-9 gap-1.5">
              {CHANNEL_ICON_OPTIONS.map((key) => {
                const I = CHANNEL_ICONS[key];
                const selected = form.icon === key;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setForm({ ...form, icon: key })}
                    className={`h-9 rounded border flex items-center justify-center transition ${selected
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200"
                      : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50"}`}
                    data-testid={`channel-icon-${key}`}
                    title={key}
                  >
                    <I className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color picker */}
          <div className="space-y-1.5">
            <Label>Colour</Label>
            <div className="flex flex-wrap gap-1.5">
              {CHANNEL_COLOR_OPTIONS.map((key) => {
                const c = CHANNEL_COLORS[key];
                const selected = form.color === key;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setForm({ ...form, color: key })}
                    className={`h-9 w-9 rounded-full border-2 flex items-center justify-center transition ${selected
                      ? "border-indigo-500 ring-2 ring-indigo-200"
                      : "border-transparent hover:border-slate-300"} ${c.bg}`}
                    data-testid={`channel-color-${key}`}
                    title={key}
                  >
                    <span className={`w-3 h-3 rounded-full ${c.dot}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: !!v })}
              data-testid="channel-active-checkbox"
            />
            Active — show this channel in the new-ticket dropdown
          </label>

          {/* Cascade rename */}
          {nameChanged && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
              <div className="text-xs text-amber-900">
                You're renaming <strong>{target.name}</strong> → <strong>{form.name}</strong>.
                {ticketCount !== null && (
                  <> Currently <strong>{ticketCount}</strong> ticket{ticketCount === 1 ? "" : "s"} reference{ticketCount === 1 ? "s" : ""} the old name.</>
                )}
              </div>
              <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer">
                <Checkbox
                  checked={cascade}
                  onCheckedChange={(v) => setCascade(!!v)}
                  disabled={ticketCount === 0}
                  data-testid="cascade-rename-checkbox"
                  className="mt-0.5"
                />
                <span>
                  Also update those {ticketCount ?? "existing"} tickets to use the new name.
                  {ticketCount === 0 && <span className="text-slate-500"> (no tickets to update)</span>}
                </span>
              </label>
            </div>
          )}

          {isWebhookWired && (
            <div className="bg-indigo-50 border border-indigo-200 rounded p-2.5 text-xs text-indigo-900 flex items-start gap-1.5">
              <Link2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>This channel is wired to an inbound webhook handler. Renaming is OK — new inbound messages will use the new name. Deactivating only hides it from the new-ticket dropdown.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="channel-submit-btn">
            {submitting ? "Saving..." : (isEdit ? "Save changes" : "Create channel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
